import React, { useState } from "react";
import { Link } from "react-router-dom";
import { commentService, Comment } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { Edit, Trash2, Save, X, MessageCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { enGB } from "date-fns/locale";
import toast from "react-hot-toast";

interface CommentItemProps {
  comment: Comment;
  onUpdate: (updatedComment: Comment) => void;
  onDelete: (commentId: string) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, onUpdate, onDelete }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isAuthor = user && user.username === comment.author_username;
  const AvatarAuthor = comment.author_avatar || "/default-avatar.png";

  const handleEdit = async () => {
    if (!editContent.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }

    if (editContent.length > 1000) {
      toast.error("Comment cannot exceed 1000 characters");
      return;
    }

    setLoading(true);
    try {
      const updatedComment = await commentService.updateComment(comment.id, editContent.trim());
      onUpdate(updatedComment);
      setIsEditing(false);
      toast.success("Comment updated successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error updating comment");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await commentService.deleteComment(comment.id);
      onDelete(comment.id);
      setShowDeleteConfirm(false);
      toast.success("Comment deleted successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Error deleting comment");
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditContent(comment.content);
  };

  if (showDeleteConfirm) {
    return (
      <div className="beat-comment rounded-lg">
        <div className="bg-red-50 border border-red-200 p-4 rounded">
          <div className="flex items-center mb-3">
            <MessageCircle className="w-5 h-5 text-red-600 mr-2" />
            <h4 className="text-red-800 font-semibold">Delete comment</h4>
          </div>
          <p className="text-red-700 mb-4">
            Are you sure you want to delete this comment? This action cannot be undone.
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="beat-button-secondary text-sm"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={loading}
              className="bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <div className="beat-spinner w-4 h-4"></div>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="beat-comment rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Link to={`/voice/${comment.author_username}`} className="flex items-center space-x-2">
            <img src={AvatarAuthor} alt={comment.author_username} className="w-7 h-7 rounded-full object-cover" />
            <span className="font-medium text-newspaper-900">{comment.author_username}</span>
          </Link>
          <span className="text-xs text-newspaper-500">
            {formatDistanceToNow(new Date(comment.created_at), {
              addSuffix: true,
              locale: enGB,
            })}
          </span>
          {comment.updated_at && new Date(comment.updated_at) > new Date(comment.created_at) && (
            <span className="text-xs text-newspaper-500 italic">(edited)</span>
          )}
        </div>

        {isAuthor && !isEditing && (
          <div className="flex space-x-2">
            <button
              onClick={() => setIsEditing(true)}
              className="text-newspaper-600 hover:text-newspaper-900 p-1"
              title="Edit comment"
            >
              <Edit className="w-3 h-3" />
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="text-red-600 hover:text-red-700 p-1"
              title="Delete comment"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="beat-input min-h-[80px] resize-none text-sm"
            placeholder="Write your comment..."
            maxLength={1000}
            disabled={loading}
          />

          <div className="flex justify-between items-center">
            <span className="text-xs text-newspaper-500">{editContent.length}/1000 characters</span>

            <div className="flex space-x-2">
              <button onClick={cancelEdit} className="beat-button-secondary text-sm px-3 py-1" disabled={loading}>
                <X className="w-3 h-3 mr-1" />
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={loading || !editContent.trim()}
                className="beat-button text-sm px-3 py-1 flex items-center space-x-1"
              >
                {loading ? (
                  <div className="beat-spinner w-3 h-3"></div>
                ) : (
                  <>
                    <Save className="w-3 h-3" />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-newspaper-800 leading-relaxed">{comment.content}</p>
      )}
    </div>
  );
};

export default CommentItem;
